/**
 * Brand asset tile (PlayAd "Brand Assets" card): media on a light checkerboard for transparent images,
 * hover → selection circle top-left, heart (favourite) + menu top-right, source badge bottom-left.
 */
import { Check, Heart, MoreHorizontal, Type } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { CreativeMedia } from "../creative/CreativeBits";
import { Dropdown } from "../ui/Overlay";
import { ASSET_SOURCE_LABEL, assetLabel, CHECKERBOARD, showsOnCheckerboard, type BrandAsset } from "./normalize";
import { safeMediaHref } from "@/lib/safe-url";

/** Loads a font asset with the FontFace API and previews it ("Aa"). Falls back to the UI font. */
export function FontPreview({ asset, className }: { asset: BrandAsset; className?: string }) {
  const family = `brand-asset-${asset.id}`;
  const [ok, setOk] = useState(false);
  useEffect(() => {
    let alive = true;
    const fontUrl = safeMediaHref(asset.url);
    if (typeof FontFace === "undefined" || !fontUrl) return;
    const ff = new FontFace(family, `url(${JSON.stringify(fontUrl)})`);
    ff.load()
      .then((loaded) => {
        document.fonts.add(loaded);
        if (alive) setOk(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [asset.url, family]);
  return (
    <div className={cn("grid size-full place-items-center bg-[#151517] text-fg", className)}>
      <div className="text-center">
        <div className="text-[44px] leading-none" style={ok ? { fontFamily: family } : undefined}>
          Aa
        </div>
        <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-muted">
          <Type className="size-3" /> {assetLabel(asset)}
        </div>
      </div>
    </div>
  );
}

export function AssetMedia({ asset, className, fit }: { asset: BrandAsset; className?: string; fit?: "cover" | "contain" }) {
  if (asset.mediaType === "font") return <FontPreview asset={asset} className={className} />;
  const board = showsOnCheckerboard(asset);
  return (
    <div className={cn("size-full", className)} style={board ? CHECKERBOARD : undefined}>
      <CreativeMedia url={asset.url} mediaType={asset.mediaType} fit={fit ?? (board ? "contain" : "cover")} className={cn("size-full", board && "bg-transparent p-3")} alt={assetLabel(asset)} />
    </div>
  );
}

export function AssetTile({
  asset,
  selected,
  selectable,
  onToggleSelect,
  onOpen,
  onFavorite,
  menu,
  showCaption = true,
  className,
}: {
  asset: BrandAsset;
  selected?: boolean;
  /** Always show the selection circle (picker mode). */
  selectable?: boolean;
  onToggleSelect?: () => void;
  onOpen?: () => void;
  onFavorite?: () => void;
  menu?: (close: () => void) => ReactNode;
  showCaption?: boolean;
  className?: string;
}) {
  const primary = onOpen ?? onToggleSelect;
  return (
    <div className={cn("group flex min-w-0 flex-col gap-1.5", className)}>
      <div
        className={cn(
          "relative aspect-square overflow-hidden rounded-xl border transition-[border-color,box-shadow]",
          selected ? "border-primary ring-2 ring-primary/60" : "border-line hover:border-line-strong",
        )}
      >
        <button type="button" onClick={primary} className="block size-full" aria-label={onOpen ? `Open ${assetLabel(asset)}` : `${selected ? "Deselect" : "Select"} ${assetLabel(asset)}`}>
          <AssetMedia asset={asset} />
        </button>
        {onToggleSelect && (
          <button
            type="button"
            role="checkbox"
            aria-checked={!!selected}
            aria-label={`Select ${assetLabel(asset)}`}
            onClick={onToggleSelect}
            className={cn(
              "absolute left-2 top-2 grid size-5.5 place-items-center rounded-full border-2 shadow-sm transition-opacity",
              selected ? "border-primary bg-primary text-white opacity-100" : "border-white/90 bg-black/25 text-transparent backdrop-blur-sm",
              !selected && !selectable && "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
            )}
          >
            <Check className="size-3" strokeWidth={3} />
          </button>
        )}
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {onFavorite && (
            <button
              type="button"
              onClick={onFavorite}
              aria-pressed={asset.favorite}
              aria-label={asset.favorite ? "Remove from favourites" : "Add to favourites"}
              className={cn(
                "grid size-6 place-items-center rounded-md bg-[#18181a]/85 backdrop-blur-sm transition-opacity hover:bg-black",
                asset.favorite ? "text-[#ff6b8b] opacity-100" : "text-white/85 opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
              )}
            >
              <Heart className="size-3.5" fill={asset.favorite ? "currentColor" : "none"} strokeWidth={2} />
            </button>
          )}
          {menu && (
            <Dropdown
              align="end"
              trigger={
                <button
                  type="button"
                  aria-label="Asset actions"
                  className="grid size-6 place-items-center rounded-md bg-[#18181a]/85 text-white/85 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black group-hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100"
                >
                  <MoreHorizontal className="size-3.5" />
                </button>
              }
            >
              {(close) => menu(close)}
            </Dropdown>
          )}
        </div>
        <span className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white/90 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
          {ASSET_SOURCE_LABEL[asset.source] ?? asset.source}
        </span>
      </div>
      {showCaption && (
        <div className="flex min-w-0 items-center gap-1 px-0.5 text-[12px]">
          <span className="truncate text-fg/90">{assetLabel(asset)}</span>
        </div>
      )}
    </div>
  );
}
